import type { PricingRule } from '@/lib/pricing';
import type {
  AdvancedBookingParams,
  BookingRestrictionParams,
  DateSpecificParams,
  DayOfWeekParams,
  ExtraGuestParams,
  GapNightParams,
  LastMinuteParams,
  MinNightsParams,
  MonthParams,
  SeasonalParams,
} from '@/lib/pricingRules';
import { MonthRuleForm } from './rules/MonthRuleForm';
import { SeasonalRuleForm } from './rules/SeasonalRuleForm';
import { DayOfWeekRuleForm } from './rules/DayOfWeekRuleForm';
import { DateSpecificRuleForm } from './rules/DateSpecificRuleForm';
import { MinNightsRuleForm } from './rules/MinNightsRuleForm';
import { BookingRestrictionRuleForm } from './rules/BookingRestrictionRuleForm';
import { GapNightRuleForm } from './rules/GapNightRuleForm';
import { LastMinuteRuleForm } from './rules/LastMinuteRuleForm';
import { AdvancedBookingRuleForm } from './rules/AdvancedBookingRuleForm';
import { ExtraGuestRuleForm } from './rules/ExtraGuestRuleForm';

type Params = Record<string, unknown>;

/**
 * RuleBody renders the typed editor for a rule's params. The wire `params`
 * JSONB IS the editable shape (no codec), so each form receives the params cast
 * to its typed view and emits the same shape back. A form accepting its typed
 * params also satisfies the `(unknown) => void` emitter by contravariance.
 */
export function RuleBody({
  type,
  params,
  onChange,
}: {
  type: PricingRule['type'];
  params: Params;
  onChange: (next: Params) => void;
}) {
  const emit = (p: unknown) => onChange(p as Params);
  switch (type) {
    case 'month':
      return <MonthRuleForm params={params as unknown as MonthParams} onChange={emit} />;
    case 'seasonal':
      return <SeasonalRuleForm params={params as unknown as SeasonalParams} onChange={emit} />;
    case 'day_of_week':
      return <DayOfWeekRuleForm params={params as unknown as DayOfWeekParams} onChange={emit} />;
    case 'date_specific':
      return <DateSpecificRuleForm params={params as unknown as DateSpecificParams} onChange={emit} />;
    case 'min_nights':
      return <MinNightsRuleForm params={params as unknown as MinNightsParams} onChange={emit} />;
    case 'booking_restriction':
      return (
        <BookingRestrictionRuleForm
          params={params as unknown as BookingRestrictionParams}
          onChange={emit}
        />
      );
    case 'gap_night':
      return <GapNightRuleForm params={params as unknown as GapNightParams} onChange={emit} />;
    case 'last_minute':
      return <LastMinuteRuleForm params={params as unknown as LastMinuteParams} onChange={emit} />;
    case 'advanced_booking':
      return (
        <AdvancedBookingRuleForm
          params={params as unknown as AdvancedBookingParams}
          onChange={emit}
        />
      );
    case 'extra_guest':
      return <ExtraGuestRuleForm params={params as unknown as ExtraGuestParams} onChange={emit} />;
    default:
      return null;
  }
}
